import { EnumEstadoFichero, EnumTipoDocumento } from '../../api'
import {
  DocumentModel,
  DocumentPatchRequest,
  DocumentPostRequest
} from '../../api/Argentia/endpoints/documents/model/document'
import {
  BlobFileModel,
  DownloadFileRequestModel,
  FilesUploadModel
} from '../../api/Pioneer/endpoints/files/models/files'
import { EnumFileExtension } from '../../api/Pioneer/enums/enumFileExtension'
import { EnumFileType } from '../../api/Pioneer/enums/enumFileType'
import { enumValueToKey } from '../../helpers'
import { generateIconsFromConfiguration } from '../../helpers/dataTableActionIconHelper'
import { formatDateOnlyStringWithTime } from '../../helpers/formatDateOnly'
import i18n from '../../i18n'
import { ActionIcons, DataTableActionConfiguration } from '../../model'
import { getActiveBalanceZone, getActiveCompany } from '../../redux'
import { EnumDocumentsNavigationFrom } from './documents.model'

export const mapDocumentToDocumentModelArray = (documents: DocumentModel[]): DocumentModel[] => {
  return documents.map(document => mapDocumentToDocumentModel(document))
}

export const mapDocumentToDocumentModel = (document: DocumentModel): DocumentModel => {
  return {
    id: document.id,
    type: enumValueToKey(EnumTipoDocumento, document.type),
    description: document.description,
    documentDate: formatDateOnlyStringWithTime(document.creationDate) ?? '',
    comments: document.comments,
    state: document.state,
    actionsConfiguration: getGlobalActionsConfiguration()
  }
}

export const mapFormDataToRequest = (
  document: DocumentModel,
  navigation: EnumDocumentsNavigationFrom,
  elementId: number
): DocumentPostRequest => {
  return {
    ...document,
    ...(navigation === EnumDocumentsNavigationFrom.Contracts && { contractId: elementId }),
    ...(navigation === EnumDocumentsNavigationFrom.Trades && { tradeId: elementId })
  }
}

export const mapFormDataToPatchRequest = (formData: any): DocumentPatchRequest => {
  return {
    id: formData.id,
    type: formData.type,
    description: formData.description,
    comments: formData.comments
  }
}

export const mapDocumentsModelToUploadFileModel = (
  document: DocumentModel[],
  type: EnumTipoDocumento,
  documentDescription?: string
): FilesUploadModel => {
  const balanceZoneId = getActiveBalanceZone()?.id || 0
  const companyId = getActiveCompany()?.id || 0
  return {
    files: document.map(doc => doc.attachment).filter(asset => asset !== undefined),
    companyId: companyId,
    balanceZoneId: balanceZoneId,
    documentType: type,
    // si se informa descripción, el back crea el G_DOCUMENTO junto con la subida (evita el createDocument aparte)
    ...(documentDescription ? { createDocumentRecord: true, documentDescription } : {})
  }
}

// -------- Documents DataTable Action Icons Configuration --------

export const getGlobalActionsConfiguration = (): Array<DataTableActionConfiguration> => {
  return [getObjectionsDisagreementAction()]
}

const getObjectionsDisagreementAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.ERROR,
    tooltip: i18n.t('documents:dataTable.tooltips.objectionsError')
  }
}

export const completeDataTableActions = (items: DocumentModel[]): DocumentModel[] => {
  return items.map(item => completeDataTableItemActions(item))
}

export const completeDataTableItemActions = (item: DocumentModel): DocumentModel => {
  return {
    ...item,
    actions:
      item.actionsConfiguration &&
      item.actionsConfiguration.length > 0 &&
      generateIconsFromConfiguration(
        item,
        filterApplicableActionsConfiguration(item, item.actionsConfiguration),
        new Map<ActionIcons, Function>([])
      )
  }
}

export const filterApplicableActionsConfiguration = (
  item: DocumentModel,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  let applicableActionsConfiguration: Array<DataTableActionConfiguration> = []
  configurations.forEach(configuration => {
    switch (configuration.icon) {
      case ActionIcons.ERROR: {
        if (item.state === EnumEstadoFichero.Error || item.state === EnumEstadoFichero.Aviso) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
    }
  })
  return applicableActionsConfiguration
}

export const mapInvoiceIdsToDownloadFileRequestModel = (
  invoiceIds: number[],
  fileExtension: EnumFileExtension = EnumFileExtension.HTML
): DownloadFileRequestModel => {
  return {
    blobFiles: invoiceIds.map(id => mapInvoiceIdToDownloadFileItem(id, fileExtension))
  }
}

const mapInvoiceIdToDownloadFileItem = (
  invoiceId: number,
  fileExtension: EnumFileExtension
): BlobFileModel => {
  return {
    documentType: EnumFileType.InvoiceDocument,
    invoiceId: invoiceId,
    fileExtension: fileExtension
  }
}

export const mapCommunicationFileToDownloadRequest = (
  id: number | undefined,
  downloadLogError?: boolean
): DownloadFileRequestModel => {
  return {
    blobFiles: [
      {
        documentType: EnumFileType.Document,
        documentId: id,
        downloadLogError: downloadLogError
      }
    ]
  }
}

export const mapProcedureDocumentToDownloadRequest = (
  procedureId: number | undefined,
  procedureDocumentId: number | undefined
): DownloadFileRequestModel => {
  return {
    blobFiles: [
      {
        documentType: EnumFileType.ProcedureDocument,
        procedureId,
        procedureDocumentId
      }
    ]
  }
}
